import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { useEffect, useState } from 'react';
import { storage } from '../../lib/firebase';

/**
 * 机上調査点 (登録済み 360度写真) の写真 URL を解決する (docs/27 §6-2 / §6-3)。
 * doc 保存済みの download URL があればそれを即時に使い、無ければ Storage path から取得する
 * (`PostsModerationDetail` と同じ getDownloadURL 方式。apps 間 import 不可のため user の usePhotoUrl は使わない)。
 */
export type StoragePhotoUrlState =
  | { status: 'idle'; url: null }
  | { status: 'loading'; url: null }
  | { status: 'ready'; url: string }
  | { status: 'error'; url: null; error: string };

const _cache = new Map<string, string>();

export function useStoragePhotoUrl(
  path: string | null | undefined,
  docUrl?: string | null,
): StoragePhotoUrlState {
  const [state, setState] = useState<StoragePhotoUrlState>(() =>
    docUrl ? { status: 'ready', url: docUrl } : { status: 'idle', url: null },
  );

  useEffect(() => {
    if (docUrl) {
      setState({ status: 'ready', url: docUrl });
      return;
    }
    if (!path) {
      setState({ status: 'idle', url: null });
      return;
    }
    const cached = _cache.get(path);
    if (cached) {
      setState({ status: 'ready', url: cached });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', url: null });
    getDownloadURL(storageRef(storage, path))
      .then((url) => {
        _cache.set(path, url);
        if (!cancelled) setState({ status: 'ready', url });
      })
      .catch((e: Error) => {
        if (!cancelled) setState({ status: 'error', url: null, error: e.message });
      });
    return () => {
      cancelled = true;
    };
  }, [path, docUrl]);

  return state;
}
